/**
 * Formats a number with thousand separators (commas)
 * @param value - The number or string to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with commas (e.g., "2,000.00")
 */
export const formatAmount = (value: number | string | null | undefined, decimals: number = 2): string => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value

  if (isNaN(numValue)) {
    return ''
  }

  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numValue)
}

/**
 * Formats a number with thousand separators without decimals
 * @param value - The number or string to format
 * @returns Formatted string with commas (e.g., "2,000")
 */
export const formatAmountNoDecimals = (value: number | string | null | undefined): string => {
  return formatAmount(value, 0)
}

/**
 * Adds thousand separators without forcing decimal places.
 * Suitable for live formatting while the user is still typing.
 * e.g. "1234" → "1,234", "1234.5" → "1,234.5"
 */
export const formatAmountLive = (value: string): string => {
  if (!value) return ''
  const negative = value.startsWith('-')
  const abs = negative ? value.slice(1) : value
  const parts = abs.split('.')
  const intPart = (parts[0] || '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const result = parts.length > 1 ? `${intPart}.${parts[1]}` : intPart
  return negative ? `-${result}` : result
}

/**
 * Parses a formatted amount string back to a number
 * Removes commas and other formatting characters
 * @param value - The formatted string (e.g., "2,000.00")
 * @returns The numeric value or empty string
 */
export const parseAmount = (value: string): string => {
  if (!value) {
    return ''
  }
  // Remove all commas and keep only numbers, decimal point, and minus sign
  return value.replace(/,/g, '')
}

/**
 * Formats a number as currency with symbol
 * @param amount - The number to format
 * @param currency - Currency code (default: 'GBP')
 * @returns Formatted currency string (e.g., "£2,000.00")
 */
export const formatCurrency = (amount: number | null | undefined, currency: string = 'GBP'): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '£0.00'
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
  }).format(amount)
}
